import servicesHero from "../../assets/images/services-hero.png";
import "./services.scss";
import { useSelector } from "react-redux";
import { domeImages, yurtImages, busImages, cabinImages } from "./RentalImages";
import Carousel from "../../components/Carousel";
import BookingCalendar from "./components/BookingCalendar";
import RentalCategoryNav from "./components/RentalCategoryNav";
import Button from "../../components/Button/Button";
import { getProperties } from "../../components/api/api";
import { useState, useEffect } from "react";

const DEFAULT_BOUNDS = { MAX_FUTURE_DAYS: 60, MAX_BOOKING_DURATION: 14 };

function resolveBookingBounds(propertyConfig) {
  return {
    MAX_FUTURE_DAYS:
      propertyConfig?.maxFutureDays ?? DEFAULT_BOUNDS.MAX_FUTURE_DAYS,
    MAX_BOOKING_DURATION:
      propertyConfig?.maxBookingDuration ?? DEFAULT_BOUNDS.MAX_BOOKING_DURATION,
  };
}

const RentalContainer = ({
  rental,
  isMobile,
  bookingBounds,
  propertyConfig,
}) => {
  const {
    id,
    title,
    price,
    description,
    images,
    hasCalendar,
    calendarProps,
    buttonProps,
  } = rental;

  return (
    <div id={id} className="rental-container">
      <Carousel images={images} fixedSize={false} />

      <div className="rental-details">
        <div className="rental-description">
          <h1>{title}</h1>

          {price && (
            <strong>
              Price:
              <br /> <span>{price}</span>
            </strong>
          )}

          <p>{description}</p>

          {buttonProps && (
            <Button onClick={buttonProps.onClick}>{buttonProps.text}</Button>
          )}
        </div>

        <div className="rental-calendar">
          {hasCalendar ? (
            <BookingCalendar
              {...calendarProps}
              propertyId={id}
              propertyConfig={propertyConfig}
              bookingBounds={bookingBounds}
            />
          ) : (
            <div className="contact-section">
              <h3>Contact Us</h3>

              <p className="contact-email">maplegrovepermaculture@gmail.com</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function Services() {
  const handleLinkClick = (url) => {
    window.open(url, "_blank", "noopener, noreferrer");
  };

  const isMobile = useSelector((state) => state.appSlice.isMobile);

  const [properties, setProperties] = useState([]);

  useEffect(() => {
    getProperties().then((data) => {
      if (Array.isArray(data)) {
        setProperties(data);
      }
    });
  }, []);

  const getPropertyConfig = (propertyId) =>
    properties.find((p) => p.propertyId === propertyId);

  const rentals = [
    {
      id: "dome",
      title: "Earth and Aircrete Dome Home",
      description:
        "Creative, charming, and cozy, this dome is constructed from aircrete and finished with clay plaster and a natural earthen floor. Equipped with all essentials for cooking, staying cozy, and sleeping deeply, it's situated close to trails for hiking and skiing that meander towards rivers and cliffs. A wood stove provides warmth, while an external composting toilet is located 15 yards outside.",
      images: domeImages,
      hasCalendar: true,
      calendarProps: {
        propertyType: "Dome",
        calendarId: process.env.REACT_APP_DOME_CALENDAR_ID,
      },

      buttonProps: {
        onClick: () =>
          handleLinkClick(
            "https://www.airbnb.ca/rooms/1041725069586524761?locale=en&_set_bev_on_new_domain=1706701673_OTUzYTdkNDcxZTA3&source_impression_id=p3_1706701674_wGkUSFcldcHG1x8B",
          ),
        text: "Learn More",
      },
    },

    {
      id: "yurt",
      title: "Forest Yurt",
      description:
        "Nestled in the trees on a large deck with the sounds of birds and squirrels all around, this unique structure spans 15 ft across with 14 triangular sides. It holds a double bed, dining table, rocking chair, 3 burner stove, sink with water, full kitchenette and all the cooking utensils you need. A wood stove radiates heat throughout the space, creating a snug and inviting atmosphere.",
      images: yurtImages,
      hasCalendar: true,
      calendarProps: {
        propertyType: "Yurt",
        calendarId: process.env.REACT_APP_YURT_CALENDAR_ID,
      },
      buttonProps: {
        onClick: () =>
          handleLinkClick(
            "https://glampinghub.com/canada/novascotia/hantscounty/yurt-rental-weekend-getaway-in-nova-scotia-canada/",
          ),
        text: "Learn More",
      },
    },

    {
      id: "bus",
      title: "Rennovated School Bus",
      description:
        "The nostalgia of a school bus meets modern comfort and style. We've transformed a classic school bus into a cozy retreat space. With high-speed onboard wifi, a propane stove, and a woodstove for when the evening chill sets in.",
      images: busImages,
      hasCalendar: false,
    },

    {
      id: "cabin",
      title: "Rustic Cabin",
      description:
        "This spacious two-story cabin, larger than our other rentals, offers a cozy wood stove, a simple kitchen, and a living area on the main floor, complemented by a loft bedroom upstairs where you can wake up to views of the treetops. Although charming, the cabin has a rustic charm with visible studs and windows lacking trim, giving it an unfinished feel. Despite this, guests consistently describe it as a peaceful and cozy retreat!",

      images: cabinImages,

      hasCalendar: false,
    },
  ];

  return (
    <div className="content-container">
      <div className="services-hero">
        {!isMobile && (
          <div className="services-hero-left">
            <img alt="dome hero" src={servicesHero} />
          </div>
        )}

        <div className="services-hero-right">
          <h1 className="display-font">Come Stay With Us!</h1>

          {isMobile && <img alt="dome hero" src={servicesHero} />}

          <p>
            Located just an hour from Halifax and a stone's throw away from the
            epic Shubenacadie River and the Bay of Fundy, our property is an
            ideal starting point for endless exploration. Whether you fancy a
            leisurely walk, a ski trip, or a scenic bike ride, the sprawling
            5-mile river trail offers a bounty of natural beauty.
          </p>

          <p className="bold">
            On-site professional massage and reiki services available with any
            rental
          </p>

          <p>
            Discover a unique getaway experience with our cozy accomodations
            nestled in nature.{" "}
          </p>
        </div>
      </div>

      <RentalCategoryNav
        rentals={rentals}
        getPropertyConfig={getPropertyConfig}
      />

      {rentals.map((rental) => {
        const propertyConfig = getPropertyConfig(rental.id);

        const bookingBounds = resolveBookingBounds(propertyConfig);

        const price = propertyConfig?.displayPrice || null;
        const title = propertyConfig?.displayName || rental.title;

        return (
          <RentalContainer
            key={rental.id}
            rental={{ ...rental, price, title }}
            isMobile={isMobile}
            propertyConfig={propertyConfig}
            bookingBounds={bookingBounds}
          />
        );
      })}
    </div>
  );
}

export default Services;
